/**
 * JewelleryBatchPrintDialog — print the tags of up to 30 jewellery pieces in
 * one go.
 *
 * Selection comes from two places (both feed the same list):
 *   • checkboxes on the Jewellery grid cards (passed in as `initialSelectedIds`)
 *   • a searchable multi-select dropdown inside this dialog
 * plus a "Select all (current filter)" shortcut that respects whatever
 * received-date / goldsmith / search filters are active on the page (the
 * caller passes the already-filtered `items`).
 *
 * Output (applied to the whole batch):
 *   • Full Tag  — complete tag card, auto layout per item (reuses the exact
 *                 single-tag renderer), honouring the Raw-material and Pieces
 *                 toggles.
 *   • QR only   — a QR sticker + jewellery number.
 *   • QR + Image — QR and photo side by side.
 * QR size (XS / S / M / L) applies to the two QR modes.
 *
 * All items flow onto an A4 sheet (multiple per page) with page-break-safe
 * wrappers so a tag is never split across pages.
 */

import { createPortal } from 'react-dom';
import React, { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import jsPDF from 'jspdf';
import { PF } from '@/lib/tagFont';
import html2canvas from 'html2canvas';
import { api } from '@/lib/api';
import {
  Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription, DialogFooter,
} from '@/components/ui/dialog';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Badge } from '@/components/ui/badge';
import { Checkbox } from '@/components/ui/checkbox';
import { Popover, PopoverContent, PopoverTrigger } from '@/components/ui/popover';
import {
  Printer, QrCode, Image as ImageIcon, FileText, Search, X, ChevronsUpDown,
  Check, CheckCheck, Loader2, Download, Eye, EyeOff, ArrowDownUp, FileSpreadsheet } from 'lucide-react';
import { toast } from 'sonner';
import { isPublicTagHost } from '@/lib/publicTagHost';
import { downloadTagsExcel, stampEstimatePdf, estimateTitle, ESTIMATE_DISCLAIMER } from '@/lib/tagExcel';
import { addSummaryTablePdf, summaryTableHtml, summaryHeightMm } from '@/lib/tagSummary';
import { rateCardById, useRateCards } from '@/lib/rateCards';
import { useGoldRate, currentGold } from '@/lib/goldRate';
import { PricingControls, usePricing, ensureRates, pricingOn, MissingRatesHost } from '@/lib/tagPricing';
import { extractError } from '@/lib/errors';
import {
  BatchTagItem, buildTagDataFromDetail, computeDefaultLayout, TAG_SIZES, TAG_LAYOUTS, CustomMmInputs, loadCustomMm,
  QR_LABEL_SIZES, openPrintWindow, presetTagUnfit, tagMaterials, TagFontControl, useTagFontScale, TagPartyToggles, useTagParty, partyKey,
} from '@/components/jewellery/JewelleryTagViewer';
import { TagMatOptToggles } from '@/components/jewellery/TagMatOptToggles';
import { useRebuildOnOpts, setMatOpts } from '@/lib/tagMatHandling';

export const BATCH_TAG_LIMIT = 30;

const OUTPUT_TYPES = [
  { id: 'full', label: 'Full Tag', icon: FileText, desc: 'Complete tag, auto layout' },
  { id: 'qr', label: 'QR only', icon: QrCode, desc: 'QR + number sticker' },
  { id: 'qrimg', label: 'QR + Image', icon: ImageIcon, desc: 'QR beside the photo' },
];

const SORT_OPTIONS = [
  { id: 'manual', label: 'Selection order' },
  { id: 'received', label: 'Received date' },
  { id: 'jewno', label: 'Jewellery no' },
];

// ── A4 packing for exact-size labels ────────────────────────────────────
const A4_MARGIN = 8;
const esc = (t) => String(t).replace(/[&<>"]/g, (c) => ({ '&': '&amp;', '<': '&lt;', '>': '&gt;', '"': '&quot;' }[c]));
const estTop = (name) => `<div style="position:absolute;left:0;right:0;top:2mm;text-align:center;font:700 10pt sans-serif;color:#1e2a4a;white-space:nowrap;overflow:hidden;text-overflow:ellipsis">${esc(estimateTitle(name))}</div>`;
const EST_BOTTOM = `<div style="position:absolute;left:0;right:0;bottom:1.5mm;text-align:center;font:italic 7pt sans-serif;color:#5b6475">${ESTIMATE_DISCLAIMER}</div>`;
const LABEL_GAP = 2;

// Tailwind's `img { display:block }` makes html2canvas draw text lower than laid out (html2canvas#2775).
const H2C_OPTS = {
  backgroundColor: '#ffffff', logging: false, useCORS: true,
};
// html2canvas measures font baselines on the live document, so patch it there during capture.
async function snap(el, scale) {
  const st = document.createElement('style');
  st.textContent = 'body > div:last-child img { display: inline-block !important; }';
  document.head.appendChild(st);
  try { return await html2canvas(el, { scale, ...H2C_OPTS }); } finally { st.remove(); }
}

function labelBoxes(root) {
  return Array.from(root.querySelectorAll('[data-testid="tag-fit-box"]')).map((el) => ({
    el, w: parseFloat(el.dataset.wmm) || 0, h: parseFloat(el.dataset.hmm) || 0,
  }));
}

// Row ("shelf") packing in selection order; returns pages of placed labels (mm).
function shelfPack(boxes, pw, ph) {
  const maxW = pw - A4_MARGIN * 2;
  const maxH = ph - A4_MARGIN * 2;
  const pages = [];
  let page = []; let x = 0; let y = 0; let rowH = 0;
  for (const b of boxes) {
    const w = Math.min(b.w, maxW); const h = Math.min(b.h, maxH);
    if (x > 0 && x + w > maxW) { x = 0; y += rowH + LABEL_GAP; rowH = 0; }
    if (y > 0 && y + h > maxH) { pages.push(page); page = []; x = 0; y = 0; rowH = 0; }
    page.push({ el: b.el, x: +(A4_MARGIN + x).toFixed(2), y: +(A4_MARGIN + y).toFixed(2), w, h });
    x += w + LABEL_GAP; rowH = Math.max(rowH, h);
  }
  if (page.length) pages.push(page);
  return pages;
}

// Bottom edge (mm) of the lowest label on the last sheet.
const lastBottom = (plan) => Math.max(0, ...(plan.pages[plan.pages.length - 1] || []).map((it) => it.y + it.h));

// Pick the A4 orientation that needs fewer sheets (portrait on a tie).
function packOnA4(boxes) {
  const portrait = shelfPack(boxes, 210, 297);
  const landscape = shelfPack(boxes, 297, 210);
  return landscape.length < portrait.length
    ? { orientation: 'landscape', pages: landscape }
    : { orientation: 'portrait', pages: portrait };
}

function MisfitPrompt({ prompt, customMm, onCancel, onChoose }) {
  if (!prompt) return null;
  return (
    <div className="fixed inset-0 z-[300] flex items-center justify-center bg-black/40 p-4" style={{ position: 'fixed' }} data-testid="batch-misfit-prompt">
      <div className="w-full max-w-md rounded-lg border border-slate-200 bg-white p-5 shadow-xl">
        <h4 className="text-base font-semibold text-[var(--ink-navy)]">Some tags don&apos;t fit {customMm.w}×{customMm.h} mm</h4>
        <p className="mt-1 text-[12px] text-slate-600">Even with the smallest readable text and every layout tried, data on these tags would be cut off:</p>
        <div className="mt-2 max-h-40 overflow-y-auto rounded-md border border-amber-200 bg-amber-50 p-2 font-mono text-[12px] text-amber-900" data-testid="batch-misfit-list">
          {prompt.list.map((n) => <div key={n}>{n}</div>)}
        </div>
        <div className="mt-4 flex flex-col gap-2">
          <Button onClick={() => onChoose('grow')} className="bg-[var(--ink-navy)] text-white hover:bg-[var(--ink-navy-2)]" data-testid="batch-misfit-grow">Print them at the smallest size that fits</Button>
          <Button variant="outline" onClick={() => onChoose('skip')} data-testid="batch-misfit-skip">Skip these tags</Button>
          <Button variant="ghost" onClick={onCancel} data-testid="batch-misfit-cancel">Cancel</Button>
        </div>
      </div>
    </div>
  );
}

export function JewelleryBatchPrintDialog({
  open,
  raised = false, // above full-screen registers (e.g. Alt + P from the Approval Register)
  onOpenChange,
  companyId,
  items = [],
  initialSelectedIds = [],
  initialRmMode,
  initialShowPieces,
  initialPreview = false,
  listName = '',
  onStoneRates,
  apiClient = api,
  batchEndpoint = '/jewellery/tags/batch',
  sizeToggle = false,
  rememberLayouts = false,
  onLayoutSaved,
}) {
  const [ids, setIds] = useState([]);
  const [mode, setMode] = useState('full');
  const [qrSizeId, setQrSizeId] = useState('sm');
  const [rmMode, setRmMode] = useState('expanded'); // 'expanded' | 'collapsed'
  const [sizeId, setSizeId] = useState('large');
  const [customMm, setCustomMm] = useState(loadCustomMm);
  // 'ask' = detect misfits and prompt; then 'skip' or 'grow' (smallest size that fits).
  const [overflow, setOverflow] = useState('ask');
  const [misfitPrompt, setMisfitPrompt] = useState(null); // { action, list: [jewelleryNo] }
  const misfitsRef = useRef({});
  const [layoutPick, setLayoutPick] = useState({}); // per-tag layout override { id: layoutId }
  const savedLayout = useMemo(() => {
    const m = {};
    if (rememberLayouts) items.forEach((it) => { if (it.tagws_layout) m[it.id] = it.tagws_layout; });
    return m;
  }, [items, rememberLayouts]);
  const tagLayout = (d) => {
    const pick = layoutPick[d.id] || savedLayout[d.id];
    if (pick && (pick !== 'auto' || isCustom)) return pick;
    return isCustom ? 'auto' : computeDefaultLayout(d, rmMode);
  };
  const pickLayout = (d, v) => {
    setLayoutPick((m) => ({ ...m, [d.id]: v }));
    setOverflow('ask');
    if (!rememberLayouts) return;
    apiClient.put(`/notes/tag-workspace/jewellery/${d.id}/layout`, { layout: v })
      .then(() => onLayoutSaved?.(d.id, v))
      .catch(() => toast.error(`Could not remember the layout for ${d.jewelleryNo}.`));
  };
  const [misfits, setMisfits] = useState({});
  const isCustom = sizeToggle && sizeId === 'custom' && mode === 'full';
  misfitsRef.current = misfits;
  const fontK = useTagFontScale();
  const pk2 = partyKey(useTagParty());
  const [fontOver, setFontOver] = useState({});
  const fontOverRef = useRef({});
  fontOverRef.current = fontOver;
  useEffect(() => { setFontOver({}); }, [fontK]);
  const onFit = useCallback((id, r) => {
    setFontOver((m) => (!!m[id] === !!r.fontOver ? m : { ...m, [id]: !!r.fontOver }));
    setMisfits((m) => {
      const bad = !r.fits && !r.fontOver;
      if (!!m[id] === bad) return m;
      const next = { ...m };
      if (bad) next[id] = true; else delete next[id];
      return next;
    });
  }, []);
  const [showPieces, setShowPieces] = useState(true);
  // Summary box after the tags (tag domain): 'none' | 'weights' | 'full'.
  const [summaryKind, setSummaryKindState] = useState(() => localStorage.getItem('tagws_batch_summary') || 'full');
  const setSummaryKind = (k) => { setSummaryKindState(k); localStorage.setItem('tagws_batch_summary', k); };
  const [showTagQr, setShowTagQr] = useState(false); // QR on the Full Tag (default OFF)
  const [sortKey, setSortKey] = useState('manual'); // manual | received | jewno
  const [busy, setBusy] = useState(false);
  const [busyLabel, setBusyLabel] = useState('');
  const [batchData, setBatchData] = useState([]);
  useRebuildOnOpts(setBatchData, buildTagDataFromDetail);
  useEffect(() => { if (open) setMatOpts([]); }, [open]);
  const [pickerOpen, setPickerOpen] = useState(false);
  const [query, setQuery] = useState('');
  const [preview, setPreview] = useState(false);
  const [actionSeq, setActionSeq] = useState(0);

  const offscreenRef = useRef(null);
  const actionRef = useRef(null);          // 'print' | 'pdf' | 'preview'
  const batchDataKeyRef = useRef('');       // id-set the current batchData was fetched for

  // Sync selection + reset transient state whenever the dialog opens.
  useEffect(() => {
    if (open) {
      setIds((initialSelectedIds || []).slice(0, BATCH_TAG_LIMIT));
      setBatchData([]);
      batchDataKeyRef.current = '';
      setBusy(false);
      setBusyLabel('');
      setQuery('');
      setPreview(false);
      actionRef.current = null;
      if (initialRmMode) { setMode('full'); setRmMode(initialRmMode); }
      if (initialShowPieces != null) setShowPieces(initialShowPieces);
      if (initialPreview && initialSelectedIds?.length) setTimeout(() => runActionRef.current?.('preview'), 80);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [open]);

  // Any option change re-detects misfits and asks again.
  useEffect(() => { setOverflow('ask'); }, [sizeId, customMm.w, customMm.h, rmMode, showPieces, showTagQr, mode]);
  const qrPx = useMemo(
    () => (QR_LABEL_SIZES.find((q) => q.id === qrSizeId) || QR_LABEL_SIZES[0]).px,
    [qrSizeId],
  );

  const labelById = useMemo(() => {
    const m = new Map();
    for (const it of items) m.set(it.id, it);
    return m;
  }, [items]);

  const filteredOptions = useMemo(() => {
    const q = query.trim().toLowerCase();
    if (!q) return items;
    return items.filter((it) => (
      (it.jewellery_no || '').toLowerCase().includes(q)
      || (it.title || '').toLowerCase().includes(q)
      || (it.jewellery_type || '').toLowerCase().includes(q)
    ));
  }, [items, query]);

  const atLimit = ids.length >= BATCH_TAG_LIMIT;

  // The print/preview/PDF order: honour the chosen sort, falling back to the
  // raw selection order. Applied client-side on the already-fetched data.
  const sortBatch = (list) => {
    const arr = [...list];
    if (sortKey === 'jewno') {
      arr.sort((a, b) => String(a.jewelleryNo || '').localeCompare(String(b.jewelleryNo || ''), undefined, { numeric: true, sensitivity: 'base' }));
    } else if (sortKey === 'received') {
      arr.sort((a, b) => String(labelById.get(a.id)?.received_at || '').localeCompare(String(labelById.get(b.id)?.received_at || '')));
    }
    return arr;
  };
  const orderedBatch = useMemo(() => sortBatch(batchData), [batchData, sortKey, labelById]); // eslint-disable-line react-hooks/exhaustive-deps
  const rawPieces = useMemo(() => ids.map((id) => labelById.get(id)).filter(Boolean), [ids, labelById]);

  const pricing = usePricing();
  const priced = isPublicTagHost();
  const goldState = useGoldRate();
  useRateCards();
  const pk = priced ? `${pricing.mode}-${pricing.cardId}-${currentGold(goldState)?.rate || ''}-${rateCardById(pricing.cardId)?.updated_at || ''}-${Object.keys(pricing.stoneRates).length}` : '';
  const runExcel = async () => {
    if (!ids.length) { toast.error('Select at least one piece.'); return; }
    setBusy(true); setBusyLabel('Building Excel…');
    try {
      const built = sortBatch(await ensureBatchData());
      if (priced && !(await ensureRates(built, 'batch', onStoneRates))) return;
      await downloadTagsExcel(built.map((d) => ({ data: d, materials: tagMaterials(d, rmMode) })), { rmMode, showPieces, withRates: priced && pricingOn(), rates: rateCardById(pricing.cardId), listName, summaryKind: priced ? summaryKind : 'full' });
      toast.success('Excel downloaded.');
    } catch (e) {
      toast.error(extractError(e, 'Could not build the Excel file.'));
    } finally { setBusy(false); setBusyLabel(''); }
  };

  const toggleId = (id) => {
    setIds((prev) => {
      if (prev.includes(id)) return prev.filter((x) => x !== id);
      if (prev.length >= BATCH_TAG_LIMIT) {
        toast.error(`You can print up to ${BATCH_TAG_LIMIT} tags at once.`);
        return prev;
      }
      return [...prev, id];
    });
  };

  const selectAllFiltered = () => {
    const all = items.map((it) => it.id);
    const capped = all.slice(0, BATCH_TAG_LIMIT);
    setIds(capped);
    if (all.length > BATCH_TAG_LIMIT) {
      toast.warning(`Selected the first ${BATCH_TAG_LIMIT} of ${all.length} filtered pieces (batch limit).`);
    }
  };

  const clearAll = () => { setIds([]); setPreview(false); };

  // Fetch the batch payload if the current id-set differs from what we already
  // have. Returns the freshly-built (or cached) tag-data array.
  const ensureBatchData = async () => {
    const key = [...ids].sort().join(',');
    if (key === batchDataKeyRef.current && batchData.length) return batchData;
    const res = await apiClient.post(batchEndpoint, {
      company_id: companyId,
      jewellery_ids: ids,
    });
    const raw = res?.data?.items || [];
    const missing = res?.data?.missing_ids || [];
    if (!raw.length) throw new Error('None of the selected pieces could be loaded.');
    const built = raw.map(buildTagDataFromDetail);
    batchDataKeyRef.current = key;
    setBatchData(built);
    if (missing.length) {
      toast.warning(`${missing.length} piece(s) could not be found and were skipped.`);
    }
    return built;
  };

  // Guard against invalid selection, load data, then schedule the action to
  // run once the off-screen sheet reflects the latest data + options.
  const runActionRef = useRef(null);
  const runAction = async (action) => {
    if (!ids.length) { toast.error('Select at least one piece.'); return; }
    if (ids.length > BATCH_TAG_LIMIT) { toast.error(`You can print up to ${BATCH_TAG_LIMIT} tags at once.`); return; }
    setBusy(true);
    setBusyLabel(action === 'pdf' ? 'Building PDF…' : action === 'preview' ? 'Loading…' : 'Preparing…');
    try {
      const built = await ensureBatchData();
      if (priced && action !== 'preview' && mode === 'full' && !(await ensureRates(built, 'batch', onStoneRates))) { setBusy(false); setBusyLabel(''); return; }
      actionRef.current = action;
      setActionSeq((s) => s + 1);
    } catch (e) {
      toast.error(extractError(e, 'Failed to prepare the batch.'));
      setBusy(false);
      setBusyLabel('');
    }
  };

  runActionRef.current = runAction;

  // Rasterise each tag node and pack them into A4 pages (row by row), never
  // splitting a tag across a page — the PDF mirrors the printed sheet and is
  // self-contained so it prints identically on any printer.
  const exportLabelPdf = async (containerEl) => {
    const boxes = labelBoxes(containerEl);
    if (!boxes.length) { toast.error('No tags fit the chosen label size.'); return; }
    const plan = packOnA4(boxes);
    const pdf = new jsPDF({ unit: 'mm', format: 'a4', orientation: plan.orientation, compress: true }); pdf.setFont(PF());
    for (let p = 0; p < plan.pages.length; p += 1) {
      if (p > 0) pdf.addPage('a4', plan.orientation);
      for (const it of plan.pages[p]) {
        // eslint-disable-next-line no-await-in-loop
        const canvas = await snap(it.el, 3);
        pdf.addImage(canvas.toDataURL('image/jpeg', 0.95), 'JPEG', it.x, it.y, it.w, it.h);
      }
    }
    if (priced) addSummaryTablePdf(pdf, orderedBatch, rmMode, summaryKind, listName, lastBottom(plan) + 4);
    stampEstimatePdf(pdf, listName);
    pdf.save(`Jewellery_Labels_${customMm.w}x${customMm.h}mm_${new Date().toISOString().slice(0, 10)}.pdf`);
    toast.success(`Downloaded ${boxes.length} label(s) on ${plan.pages.length} A4 ${plan.orientation} page(s).`);
  };

  const exportPdf = async (containerEl, count) => {
    if (isCustom) { await exportLabelPdf(containerEl); return; }
    const nodes = Array.from(containerEl.children);
    const pdf = new jsPDF({ unit: 'mm', format: 'a4', compress: true }); pdf.setFont(PF());
    const PX2MM = 25.4 / 96;
    const margin = 8;
    const contentW = 210 - margin * 2;
    const contentH = 297 - margin * 2;
    const gap = 3;
    let x = 0; let y = 0; let rowH = 0; let placed = 0;
    for (const node of nodes) {
      // eslint-disable-next-line no-await-in-loop
      const canvas = await snap(node, 2);
      const wMM = (canvas.width / 2) * PX2MM;
      const hMM = (canvas.height / 2) * PX2MM;
      if (placed > 0 && x + wMM > contentW + 0.1) { x = 0; y += rowH + gap; rowH = 0; }
      if (y + hMM > contentH + 0.1 && y > 0) { pdf.addPage(); x = 0; y = 0; rowH = 0; }
      pdf.addImage(canvas.toDataURL('image/jpeg', 0.92), 'JPEG', margin + x, margin + y, wMM, hMM);
      x += wMM + gap;
      rowH = Math.max(rowH, hMM);
      placed += 1;
    }
    if (priced) addSummaryTablePdf(pdf, orderedBatch, rmMode, summaryKind, listName, placed ? margin + y + rowH + 4 : undefined);
    stampEstimatePdf(pdf, listName);
    pdf.save(`Jewellery_Tags_${new Date().toISOString().slice(0, 10)}.pdf`);
    toast.success(`Downloaded PDF with ${count} tag(s).`);
  };

  // Runs the scheduled action after the off-screen sheet has painted. Two RAFs
  // let the QR SVGs + images lay out before we snapshot / rasterise.
  useEffect(() => {
    if (!actionSeq) return undefined;
    const action = actionRef.current;
    if (!action) return undefined;
    if (!orderedBatch.length) return undefined;
    const el = offscreenRef.current;
    if (!el) return undefined;
    let raf2;
    const raf1 = requestAnimationFrame(() => {
      raf2 = requestAnimationFrame(async () => {
        try {
          // Wait until every label has finished fitting (growing happens over a few frames).
          for (let t = 0; t < 600 && el.querySelector('[data-testid="tag-fit-box"][data-fits=""]'); t += 1) {
            // eslint-disable-next-line no-await-in-loop
            await new Promise((r) => requestAnimationFrame(r));
          }
          const fo = orderedBatch.filter((d) => fontOverRef.current[d.id]).map((d) => d.jewelleryNo);
          if (isCustom && fo.length && (action === 'print' || action === 'pdf')) {
            toast.error(`The text at ${Math.round(fontK * 100)}% goes outside these tags: ${fo.join(', ')}. Reduce the text size (A−) to print.`);
            return;
          }
          const bad = orderedBatch.filter((d) => misfitsRef.current[d.id]);
          if (isCustom && overflow === 'ask' && bad.length && (action === 'print' || action === 'pdf')) {
            setMisfitPrompt({ action, list: orderedBatch.filter((d) => misfitsRef.current[d.id]).map((d) => d.jewelleryNo) });
            return;
          }
          if (!isCustom && mode === 'full' && (action === 'print' || action === 'pdf')) {
            const unfit = Array.from(el.querySelectorAll('[data-testid="tag-preset-box"]'))
              .map((n, i) => (presetTagUnfit(n) ? orderedBatch[i]?.jewelleryNo : null)).filter(Boolean);
            if (unfit.length) {
              toast.error(fontK !== 1
                ? `The text at ${Math.round(fontK * 100)}% goes outside these tags: ${unfit.join(', ')}. Reduce the text size (A−) to print.`
                : `These tags don't fit the chosen size — data would be cut off: ${unfit.join(', ')}. Use a larger size or a custom mm size.`);
              return;
            }
          }
          if (action === 'print' && isCustom) {
            const boxes = labelBoxes(el);
            const plan = packOnA4(boxes);
            const [pw, ph] = plan.orientation === 'landscape' ? [297, 210] : [210, 297];
            const sumOn = priced && summaryKind !== 'none';
            const top = lastBottom(plan) + 2;
            // Summary sits under the last tags when there is room on that sheet, else on its own sheet.
            const inline = sumOn && top + summaryHeightMm(orderedBatch, rmMode, summaryKind) * 1.2 + 12 <= ph - 10;
            const sumHtml = sumOn ? summaryTableHtml(orderedBatch, rmMode, summaryKind, listName) : '';
            const html = plan.pages.map((pg, pi) => (
              `<div style="position:relative;width:${pw}mm;height:${ph}mm;overflow:hidden;page-break-after:always;break-after:page">${estTop(listName)}${EST_BOTTOM}${
                pg.map((it) => `<div style="position:absolute;left:${it.x}mm;top:${it.y}mm">${it.el.outerHTML}</div>`).join('')
              }${inline && pi === plan.pages.length - 1 ? `<div style="position:absolute;left:12mm;right:12mm;top:${top}mm">${sumHtml}</div>` : ''}</div>`
            )).join('') + (sumOn && !inline ? `<div style="width:${pw}mm;min-height:${ph}mm;padding:12mm;box-sizing:border-box">${sumHtml}</div>` : '');
            openPrintWindow(html, {
              pageSize: `A4 ${plan.orientation}`, margin: '0', title: `Jewellery Tags (${boxes.length})`,
            });
            toast.success(`Prepared ${boxes.length} label(s) on ${plan.pages.length} A4 ${plan.orientation} page(s).`);
          } else if (action === 'print') {
            openPrintWindow(`<div style="text-align:center;font:700 13px sans-serif;color:#1e2a4a;margin-bottom:4mm">${esc(estimateTitle(listName))}</div>${el.innerHTML}${priced && summaryKind !== 'none' ? summaryTableHtml(orderedBatch, rmMode, summaryKind, listName) : ''}<div style="text-align:center;font:italic 10px sans-serif;color:#5b6475;margin-top:4mm">${ESTIMATE_DISCLAIMER}</div>`, {
              pageSize: 'A4', margin: '8mm',
              title: `Jewellery Tags (${orderedBatch.length})`,
            });
            toast.success(`Prepared ${orderedBatch.length} tag(s) for printing.`);
          } else if (action === 'pdf') {
            await exportPdf(el, orderedBatch.length);
          } else if (action === 'preview') {
            setPreview(true);
          }
        } catch (err) {
          toast.error(action === 'pdf' ? 'Could not build the PDF.' : 'Could not open the print window. Please allow pop-ups.');
        } finally {
          actionRef.current = null;
          setBusy(false);
          setBusyLabel('');
        }
      });
    });
    return () => { cancelAnimationFrame(raf1); if (raf2) cancelAnimationFrame(raf2); };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [actionSeq]);

  // If the selection changes while a preview is showing, refresh it.
  useEffect(() => {
    if (!preview) return;
    const key = [...ids].sort().join(',');
    if (key !== batchDataKeyRef.current) {
      if (!ids.length) { setPreview(false); return; }
      runAction('preview');
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [ids]);

  const isQrMode = mode === 'qr' || mode === 'qrimg';

  return (
    <Dialog open={open} onOpenChange={(o) => { if (!busy) onOpenChange?.(o); }}>
      {raised && open ? createPortal(<div className="fixed inset-0 z-[390] bg-black/60" data-testid="batch-raised-backdrop" />, document.body) : null}
      <DialogContent className={`max-w-3xl max-h-[92vh] overflow-y-auto overflow-x-hidden ${raised ? 'z-[400]' : ''}`} data-testid="jewellery-batch-print-dialog">
        {priced ? <MissingRatesHost where="batch" contained /> : null}
        <DialogHeader>
          <DialogTitle className="flex items-center gap-2">
            <Printer size={18} className="text-[var(--jewellery-icon)]" />
            Batch Print Tags{listName ? <span className="ml-1 font-normal text-muted-foreground" data-testid="batch-list-name"> · List: {listName}</span> : null}
          </DialogTitle>
          <DialogDescription>
            Pick up to {BATCH_TAG_LIMIT} pieces, choose what to print, then print them all on an A4 sheet.
            Scanning any QR opens the public tag{priced ? "; prices appear on tags only when you switch them on below." : ". No value or price is ever shown."}
          </DialogDescription>
        </DialogHeader>

        <div className="space-y-5 py-1">
          {/* ── Selection ─────────────────────────────────────────── */}
          <section className="space-y-2">
            <div className="flex items-center justify-between">
              <span className="text-[11px] uppercase tracking-wide text-slate-500 font-medium">Pieces to print</span>
              <Badge
                variant="outline"
                className={`font-mono text-[11px] ${atLimit ? 'border-amber-300 text-amber-700 bg-amber-50' : ''}`}
                data-testid="batch-selected-count"
              >
                {ids.length} / {BATCH_TAG_LIMIT}
              </Badge>
            </div>

            <div className="flex flex-wrap items-center gap-2">
              <Popover open={pickerOpen} onOpenChange={setPickerOpen}>
                <PopoverTrigger asChild>
                  <Button
                    type="button"
                    variant="outline"
                    role="combobox"
                    aria-expanded={pickerOpen}
                    className="h-9 justify-between font-normal flex-1 min-w-[220px] text-slate-600"
                    data-testid="batch-multiselect-trigger"
                  >
                    <span className="flex items-center gap-2">
                      <Search size={14} className="text-slate-400" />
                      Add / remove pieces…
                    </span>
                    <ChevronsUpDown size={14} className="text-slate-400" />
                  </Button>
                </PopoverTrigger>
                <PopoverContent className={`p-0 w-[var(--radix-popover-trigger-width)] max-w-[520px] ${raised ? 'z-[410]' : ''}`} align="start">
                  <div className="border-b border-[var(--divider)] p-2">
                    <Input
                      autoFocus
                      value={query}
                      onChange={(e) => setQuery(e.target.value)}
                      placeholder="Search by number, title or type…"
                      className="h-8 text-sm"
                      data-testid="batch-multiselect-search"
                    />
                  </div>
                  <div className="max-h-64 overflow-y-auto py-1">
                    {filteredOptions.length === 0 ? (
                      <div className="px-3 py-4 text-center text-xs text-slate-500" data-testid="batch-multiselect-empty">
                        {items.length === 0 ? 'No jewellery available.' : 'No matches.'}
                      </div>
                    ) : (
                      filteredOptions.map((it) => {
                        const checked = ids.includes(it.id);
                        const disabled = !checked && atLimit;
                        return (
                          <button
                            key={it.id}
                            type="button"
                            onClick={() => toggleId(it.id)}
                            disabled={disabled}
                            className={[
                              'w-full flex items-center gap-2.5 px-3 py-2 text-left text-sm transition-colors',
                              disabled ? 'opacity-40 cursor-not-allowed' : 'hover:bg-slate-50',
                              checked ? 'bg-[var(--jewellery-bg)]/40' : '',
                            ].join(' ')}
                            data-testid={`batch-option-${it.id}`}
                          >
                            <Checkbox checked={checked} className="pointer-events-none" tabIndex={-1} />
                            <span className="min-w-0 flex-1">
                              <span className="font-mono text-[12px] text-emerald-700">{it.jewellery_no}</span>
                              {it.title ? <span className="text-slate-600"> · {it.title}</span> : null}
                            </span>
                          </button>
                        );
                      })
                    )}
                  </div>
                </PopoverContent>
              </Popover>

              <Button
                type="button"
                variant="outline"
                size="sm"
                onClick={selectAllFiltered}
                className="h-9"
                disabled={items.length === 0}
                data-testid="batch-select-all"
                title="Select all pieces in the current page filter (up to the limit)"
              >
                <CheckCheck size={14} className="mr-1.5" /> Select all (filtered)
              </Button>
              {ids.length > 0 && (
                <Button
                  type="button"
                  variant="ghost"
                  size="sm"
                  onClick={clearAll}
                  className="h-9 text-slate-500"
                  data-testid="batch-clear"
                >
                  <X size={13} className="mr-1" /> Clear
                </Button>
              )}
            </div>

            {/* Selected chips */}
            {ids.length > 0 ? (
              <div className="flex flex-wrap gap-1.5 rounded-md border border-slate-200 bg-slate-50/60 p-2 max-h-28 overflow-y-auto" data-testid="batch-selected-chips">
                {ids.map((id) => {
                  const it = labelById.get(id);
                  return (
                    <span
                      key={id}
                      className="inline-flex items-center gap-1 rounded-full border border-[var(--jewellery-border)] bg-white pl-2 pr-1 py-0.5 text-[11px] font-mono text-[var(--ink-navy)]"
                      data-testid={`batch-chip-${id}`}
                    >
                      {it?.jewellery_no || id.slice(0, 6)}
                      <button
                        type="button"
                        onClick={() => toggleId(id)}
                        className="inline-flex items-center justify-center w-4 h-4 rounded-full text-slate-400 hover:text-rose-600 hover:bg-rose-50 transition-colors"
                        aria-label={`Remove ${it?.jewellery_no || id}`}
                        data-testid={`batch-chip-remove-${id}`}
                      >
                        <X size={11} />
                      </button>
                    </span>
                  );
                })}
              </div>
            ) : (
              <div className="rounded-md border border-dashed border-slate-200 p-3 text-center text-[11px] text-slate-400">
                Nothing selected yet. Use the dropdown above, "Select all (filtered)", or tick cards on the Jewellery page.
              </div>
            )}
          </section>

          {/* ── Output type ─────────────────────────────────────────── */}
          <section className="space-y-2">
            <span className="text-[11px] uppercase tracking-wide text-slate-500 font-medium">What to print</span>
            <div className="grid grid-cols-3 gap-2" data-testid="batch-output-type">
              {OUTPUT_TYPES.map((t) => {
                const active = mode === t.id;
                const Icon = t.icon;
                return (
                  <button
                    key={t.id}
                    type="button"
                    onClick={() => setMode(t.id)}
                    aria-pressed={active}
                    data-testid={`batch-output-${t.id}`}
                    className={[
                      'rounded-md border px-3 py-2.5 text-left transition-colors',
                      'focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-[var(--jewellery-icon)]',
                      active
                        ? 'border-[var(--jewellery-icon)] bg-[var(--jewellery-bg)]'
                        : 'border-slate-200 bg-white hover:border-[var(--jewellery-border)] hover:bg-slate-50',
                    ].join(' ')}
                  >
                    <div className={`flex items-center gap-1.5 text-[12px] font-medium ${active ? 'text-[var(--jewellery-icon)]' : 'text-[var(--ink-navy)]'}`}>
                      <Icon size={14} /> {t.label}
                    </div>
                    <div className="text-[10px] text-slate-500 mt-0.5 leading-tight">{t.desc}</div>
                  </button>
                );
              })}
            </div>
          </section>

          {priced ? (
            <section className="space-y-2">
              <span className="text-[11px] uppercase tracking-wide text-slate-500 font-medium">Summary at the end</span>
              <div className="grid grid-cols-3 gap-2" data-testid="batch-summary-kind">
                {[['none', 'No summary', 'Only the tags'], ['weights', 'Gross & Net', 'Weight totals per purity'], ['full', 'With raw materials', 'Plus diamonds, colour stones & pearls']].map(([id, label, desc]) => (
                  <button key={id} type="button" onClick={() => setSummaryKind(id)} aria-pressed={summaryKind === id} data-testid={`batch-summary-${id}`}
                    className={`rounded-md border px-3 py-2 text-left transition-colors ${summaryKind === id ? 'border-[var(--jewellery-icon)] bg-[var(--jewellery-bg)]' : 'border-slate-200 bg-white hover:bg-slate-50'}`}>
                    <div className={`flex items-center gap-1.5 text-[12px] font-medium ${summaryKind === id ? 'text-[var(--jewellery-icon)]' : 'text-[var(--ink-navy)]'}`}>
                      <span className={`grid h-3.5 w-3.5 place-items-center rounded-sm border ${summaryKind === id ? 'border-[var(--jewellery-icon)] bg-[var(--jewellery-icon)] text-white' : 'border-slate-300'}`}>{summaryKind === id ? <Check size={10} /> : null}</span>{label}
                    </div>
                    <div className="mt-0.5 text-[10px] leading-tight text-slate-500">{desc}</div>
                  </button>
                ))}
              </div>
            </section>
          ) : null}

          {/* ── Options (same set as single-tag printing) ────────────
              Both groups are always visible so the panel mirrors the
              single-tag printer. Each group is active only where it has an
              effect: Raw material + Pieces apply to Full Tag; QR size applies
              to QR only / QR + Image. The inactive group is dimmed. */}
          <section className="space-y-3">
            {/* Raw material + Pieces (Full Tag) */}
            <div
              className={`flex flex-wrap items-center gap-x-6 gap-y-2 transition-opacity ${mode === 'full' ? '' : 'opacity-45'}`}
              aria-disabled={mode !== 'full'}
            >
              <span className="text-[10px] uppercase tracking-wide text-slate-400 w-full -mb-1">Full Tag options</span>
              {sizeToggle ? (
                <div className="flex items-center gap-2">
                  <span className="text-[11px] text-slate-500">Tag size:</span>
                  <div className="inline-flex rounded-md border border-slate-200 overflow-hidden" data-testid="batch-tag-size">
                    {TAG_SIZES.map((z, idx) => (
                      <button
                        key={z.id}
                        type="button"
                        disabled={mode !== 'full'}
                        onClick={() => setSizeId(z.id)}
                        aria-pressed={sizeId === z.id}
                        data-testid={`batch-tag-size-${z.id}`}
                        className={`px-2.5 py-1 text-[11px] transition-colors disabled:cursor-not-allowed ${idx > 0 ? 'border-l border-slate-200' : ''} ${sizeId === z.id ? 'bg-[var(--jewellery-bg)] text-[var(--jewellery-icon)]' : 'bg-white text-slate-600 hover:bg-slate-50'}`}
                      >
                        {z.label}
                      </button>
                    ))}
                    <button
                      type="button"
                      disabled={mode !== 'full'}
                      onClick={() => setSizeId('custom')}
                      aria-pressed={sizeId === 'custom'}
                      data-testid="batch-tag-size-custom"
                      className={`px-2.5 py-1 text-[11px] border-l border-slate-200 transition-colors disabled:cursor-not-allowed ${sizeId === 'custom' ? 'bg-[var(--jewellery-bg)] text-[var(--jewellery-icon)]' : 'bg-white text-slate-600 hover:bg-slate-50'}`}
                    >
                      Custom (mm)
                    </button>
                  </div>
                  {sizeId === 'custom' ? <CustomMmInputs value={customMm} onChange={setCustomMm} testPrefix="batch" /> : null}
                </div>
              ) : null}
              {isCustom && orderedBatch.some((d) => misfits[d.id]) ? (
                <div className="w-full rounded-md border border-amber-300 bg-amber-50 px-2.5 py-1.5 text-[11px] text-amber-800" data-testid="batch-misfit-warning">
                  {orderedBatch.filter((d) => misfits[d.id]).length} tag(s) don&apos;t fit {customMm.w}×{customMm.h} mm even at the smallest readable text
                  {' '}({orderedBatch.filter((d) => misfits[d.id]).map((d) => d.jewelleryNo).join(', ')}). You&apos;ll be asked to skip them or print them at the smallest size that fits.
                </div>
              ) : null}
              <div className="flex items-center gap-2">
                <span className="text-[11px] text-slate-500">Raw material:</span>
                <div className="inline-flex rounded-md border border-slate-200 overflow-hidden" data-testid="batch-rm-mode">
                  <button
                    type="button"
                    disabled={mode !== 'full'}
                    onClick={() => setRmMode('expanded')}
                    aria-pressed={rmMode === 'expanded'}
                    data-testid="batch-rm-expanded"
                    className={`px-2.5 py-1 text-[11px] transition-colors disabled:cursor-not-allowed ${rmMode === 'expanded' ? 'bg-[var(--jewellery-bg)] text-[var(--jewellery-icon)]' : 'bg-white text-slate-600 hover:bg-slate-50'}`}
                  >
                    Expanded
                  </button>
                  <button
                    type="button"
                    disabled={mode !== 'full'}
                    onClick={() => setRmMode('collapsed')}
                    aria-pressed={rmMode === 'collapsed'}
                    data-testid="batch-rm-collapsed"
                    className={`px-2.5 py-1 text-[11px] border-l border-slate-200 transition-colors disabled:cursor-not-allowed ${rmMode === 'collapsed' ? 'bg-[var(--jewellery-bg)] text-[var(--jewellery-icon)]' : 'bg-white text-slate-600 hover:bg-slate-50'}`}
                  >
                    Collapsed
                  </button>
                  {isPublicTagHost() ? (
                    <button
                      type="button"
                      disabled={mode !== 'full'}
                      onClick={() => setRmMode('raw')}
                      aria-pressed={rmMode === 'raw'}
                      data-testid="batch-rm-raw"
                      title="Every line as-is, no tag rules"
                      className={`px-2.5 py-1 text-[11px] border-l border-slate-200 transition-colors disabled:cursor-not-allowed ${rmMode === 'raw' ? 'bg-[var(--jewellery-bg)] text-[var(--jewellery-icon)]' : 'bg-white text-slate-600 hover:bg-slate-50'}`}
                    >
                      Raw
                    </button>
                  ) : null}
                </div>
              </div>
              <div className="flex items-center gap-2">
                <span className="text-[11px] text-slate-500">Pieces:</span>
                <div className="inline-flex rounded-md border border-slate-200 overflow-hidden" data-testid="batch-pieces">
                  <button
                    type="button"
                    disabled={mode !== 'full'}
                    onClick={() => setShowPieces(true)}
                    aria-pressed={showPieces}
                    data-testid="batch-pieces-on"
                    className={`px-2.5 py-1 text-[11px] transition-colors disabled:cursor-not-allowed ${showPieces ? 'bg-[var(--jewellery-bg)] text-[var(--jewellery-icon)]' : 'bg-white text-slate-600 hover:bg-slate-50'}`}
                  >
                    With pieces
                  </button>
                  <button
                    type="button"
                    disabled={mode !== 'full'}
                    onClick={() => setShowPieces(false)}
                    aria-pressed={!showPieces}
                    data-testid="batch-pieces-off"
                    className={`px-2.5 py-1 text-[11px] border-l border-slate-200 transition-colors disabled:cursor-not-allowed ${!showPieces ? 'bg-[var(--jewellery-bg)] text-[var(--jewellery-icon)]' : 'bg-white text-slate-600 hover:bg-slate-50'}`}
                  >
                    Without pieces
                  </button>
                </div>
              </div>
              <div className="flex items-center gap-2">
                <span className="text-[11px] text-slate-500">QR on tag:</span>
                <div className="inline-flex rounded-md border border-slate-200 overflow-hidden" data-testid="batch-tag-qr">
                  <button
                    type="button"
                    disabled={mode !== 'full'}
                    onClick={() => setShowTagQr(true)}
                    aria-pressed={showTagQr}
                    data-testid="batch-tag-qr-on"
                    className={`px-2.5 py-1 text-[11px] transition-colors disabled:cursor-not-allowed ${showTagQr ? 'bg-[var(--jewellery-bg)] text-[var(--jewellery-icon)]' : 'bg-white text-slate-600 hover:bg-slate-50'}`}
                  >
                    With QR
                  </button>
                  <button
                    type="button"
                    disabled={mode !== 'full'}
                    onClick={() => setShowTagQr(false)}
                    aria-pressed={!showTagQr}
                    data-testid="batch-tag-qr-off"
                    className={`px-2.5 py-1 text-[11px] border-l border-slate-200 transition-colors disabled:cursor-not-allowed ${!showTagQr ? 'bg-[var(--jewellery-bg)] text-[var(--jewellery-icon)]' : 'bg-white text-slate-600 hover:bg-slate-50'}`}
                  >
                    Without QR
                  </button>
                </div>
              </div>
            </div>

            {/* QR size (QR only / QR + Image) */}
            <div
              className={`flex items-center gap-2 flex-wrap transition-opacity ${isQrMode ? '' : 'opacity-45'}`}
              aria-disabled={!isQrMode}
            >
              <span className="text-[10px] uppercase tracking-wide text-slate-400 w-full -mb-1">QR only / QR + Image options</span>
              <span className="text-[11px] text-slate-500">QR size:</span>
              <div className="inline-flex rounded-md border border-slate-200 overflow-hidden" data-testid="batch-qr-size">
                {QR_LABEL_SIZES.map((q, i) => (
                  <button
                    key={q.id}
                    type="button"
                    disabled={!isQrMode}
                    onClick={() => setQrSizeId(q.id)}
                    aria-pressed={qrSizeId === q.id}
                    data-testid={`batch-qr-size-${q.id}`}
                    className={`px-2.5 py-1 text-[11px] transition-colors disabled:cursor-not-allowed ${i > 0 ? 'border-l border-slate-200' : ''} ${qrSizeId === q.id ? 'bg-[var(--jewellery-bg)] text-[var(--jewellery-icon)]' : 'bg-white text-slate-600 hover:bg-slate-50'}`}
                  >
                    {q.label}
                  </button>
                ))}
              </div>
            </div>
          </section>

          {/* ── Order (sort) + Preview ──────────────────────────────── */}
          <section className="flex flex-wrap items-center gap-3">
            <div className="flex items-center gap-2">
              <ArrowDownUp size={13} className="text-slate-400" />
              <span className="text-[11px] text-slate-500">Order:</span>
              <div className="inline-flex rounded-md border border-slate-200 overflow-hidden" data-testid="batch-sort">
                {SORT_OPTIONS.map((s, i) => (
                  <button
                    key={s.id}
                    type="button"
                    onClick={() => setSortKey(s.id)}
                    aria-pressed={sortKey === s.id}
                    data-testid={`batch-sort-${s.id}`}
                    className={`px-2.5 py-1 text-[11px] transition-colors ${i > 0 ? 'border-l border-slate-200' : ''} ${sortKey === s.id ? 'bg-[var(--jewellery-bg)] text-[var(--jewellery-icon)]' : 'bg-white text-slate-600 hover:bg-slate-50'}`}
                  >
                    {s.label}
                  </button>
                ))}
              </div>
            </div>
            {mode === 'full' ? <div className="ml-auto flex flex-wrap items-center gap-2"><TagMatOptToggles testid="batch-mat-opts" pieces={rawPieces} /><TagPartyToggles testid="batch-party" /><TagFontControl testid="batch-font" /></div> : <div className="ml-auto"><TagMatOptToggles testid="batch-mat-opts" pieces={rawPieces} /></div>}
            <Button
              type="button"
              variant="outline"
              size="sm"
              onClick={() => (preview ? setPreview(false) : runAction('preview'))}
              disabled={busy || ids.length === 0}
              className={`h-8 ${mode === 'full' ? '' : 'ml-auto'}`}
              data-testid="batch-preview-toggle"
            >
              {preview ? (<><EyeOff size={13} className="mr-1.5" /> Hide preview</>) : (<><Eye size={13} className="mr-1.5" /> Preview sheet</>)}
            </Button>
          </section>

          {isCustom && orderedBatch.some((d) => fontOver[d.id]) ? (
            <div className="rounded-md border border-red-300 bg-red-50 px-3 py-2 text-[12px] font-bold text-red-600" data-testid="batch-font-over">
              Text at {Math.round(fontK * 100)}% goes outside: {orderedBatch.filter((d) => fontOver[d.id]).map((d) => d.jewelleryNo).join(', ')} — reduce the text size (A−) to print.
            </div>
          ) : null}
          {/* ── Sheet preview (scaled A4) ───────────────────────────── */}
          {preview && orderedBatch.length > 0 ? (
            <section
              className="rounded-md border border-slate-200 bg-slate-100 p-2 overflow-auto"
              style={{ maxHeight: 340 }}
              data-testid="batch-preview-panel"
            >
              <div style={{ zoom: 0.5 }}>
                <div style={{ width: 760, margin: '0 auto', background: '#fff', padding: 10, boxShadow: '0 1px 4px rgba(15,23,42,0.12)' }}>
                  {orderedBatch.map((d) => (
                    <div key={`pvw-${d.id}`} style={{ display: 'inline-block', verticalAlign: 'top' }}>
                    {mode === 'full' ? (
                      <select
                        value={tagLayout(d)}
                        onChange={(e) => pickLayout(d, e.target.value)}
                        style={{ display: 'block', margin: '6px 6px 0', fontSize: 18, padding: '2px 6px', border: '1px solid #CBD5E1', borderRadius: 6 }}
                        data-testid={`batch-tag-layout-select-${d.jewelleryNo}`}
                        aria-label={`Layout for ${d.jewelleryNo}`}
                      >
                        {isCustom ? <option value="auto">Auto (best fit)</option> : null}
                        {TAG_LAYOUTS.map((l) => <option key={l.id} value={l.id}>{l.label}</option>)}
                      </select>
                    ) : null}
                    <BatchTagItem
                      data={d}
                      mode={mode}
                      layoutId={mode === 'full' ? tagLayout(d) : undefined}
                      rmMode={rmMode}
                      showPieces={showPieces}
                      showQr={showTagQr}
                      qrPx={qrPx}
                      sizeId={sizeId}
                      customMm={customMm}
                      overflow={overflow}
                      fontScale={fontK}
                      priced={priced && mode === 'full'}
                      key={`pv-${d.id}-${sizeId}-${customMm.w}x${customMm.h}-${overflow}-${rmMode}-${showPieces}-${showTagQr}-${tagLayout(d)}-${pk}-${fontK}-${pk2}`}
                    />
                    </div>
                  ))}
                  {priced && summaryKind !== 'none' ? <div data-testid="batch-preview-summary" dangerouslySetInnerHTML={{ __html: summaryTableHtml(orderedBatch, rmMode, summaryKind, listName) }} /> : null}
                </div>
              </div>
            </section>
          ) : null}

          <p className="text-[11px] text-slate-500 leading-snug">
            Tags flow onto A4, multiple per page, and are never split across a page break.
            Full tags pick the best layout for each piece automatically — use Preview to change the layout of any single tag. The PDF prints
            identically on any printer.
          </p>
        </div>

        <DialogFooter className="flex-wrap gap-2 sm:flex-wrap sm:space-x-0">
          <Button
            variant="outline"
            onClick={() => onOpenChange?.(false)}
            disabled={busy}
            data-testid="batch-cancel"
          >
            Cancel
          </Button>
          {priced ? <PricingControls up testid="batch-pricing" /> : null}
          {isPublicTagHost() ? (
          <Button
            variant="outline"
            onClick={runExcel}
            disabled={busy || ids.length === 0}
            className="border-[var(--jewellery-border)] text-[var(--jewellery-icon)] hover:bg-[var(--jewellery-bg)]"
            data-testid="batch-download-excel"
          >
            {busy && busyLabel.startsWith('Building Excel') ? <><Loader2 size={15} className="mr-1.5 animate-spin" /> Building…</> : <><FileSpreadsheet size={15} className="mr-1.5" /> Download Excel</>}
          </Button>
          ) : null}
          <Button
            variant="outline"
            onClick={() => runAction('pdf')}
            disabled={busy || ids.length === 0}
            className="border-[var(--jewellery-border)] text-[var(--jewellery-icon)] hover:bg-[var(--jewellery-bg)]"
            data-testid="batch-download-pdf"
          >
            {busy && busyLabel.startsWith('Building PDF') ? (
              <><Loader2 size={15} className="mr-1.5 animate-spin" /> Building…</>
            ) : (
              <><Download size={15} className="mr-1.5" /> Download PDF</>
            )}
          </Button>
          <Button
            onClick={() => runAction('print')}
            disabled={busy || ids.length === 0}
            className="bg-[var(--ink-navy)] hover:bg-[var(--ink-navy-2)] text-white"
            data-testid="batch-print-confirm"
          >
            {busy && busyLabel === 'Preparing…' ? (
              <><Loader2 size={15} className="mr-1.5 animate-spin" /> Preparing…</>
            ) : (
              <><Printer size={15} className="mr-1.5" /> Print {ids.length || ''} {ids.length === 1 ? 'tag' : 'tags'}</>
            )}
          </Button>
        </DialogFooter>

        {/* Off-screen print sheet — rendered from the fetched batch data using
            the exact single-tag renderer so output matches the preview. */}
        <div style={{ position: 'absolute', left: -99999, top: 0, width: 734 }} aria-hidden="true">
          <div ref={offscreenRef}>
            {orderedBatch.map((d) => (
              <BatchTagItem
                data={d}
                mode={mode}
                layoutId={mode === 'full' ? tagLayout(d) : undefined}
                rmMode={rmMode}
                showPieces={showPieces}
                showQr={showTagQr}
                qrPx={qrPx}
                sizeId={sizeId}
                customMm={customMm}
                overflow={overflow}
                onFit={onFit}
                fontScale={fontK}
                priced={priced && mode === 'full'}
                key={`${d.id}-${sizeId}-${customMm.w}x${customMm.h}-${overflow}-${rmMode}-${showPieces}-${showTagQr}-${tagLayout(d)}-${pk}-${fontK}-${pk2}`}
              />
            ))}
          </div>
        </div>
      <MisfitPrompt
        prompt={misfitPrompt} customMm={customMm}
        onCancel={() => setMisfitPrompt(null)}
        onChoose={(choice) => { const act = misfitPrompt.action; setMisfitPrompt(null); setOverflow(choice); setTimeout(() => runAction(act), 0); }}
      />
      </DialogContent>
    </Dialog>
  );
}

export default JewelleryBatchPrintDialog;
